import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import useLanguage from "../../hooks/useLanguage";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import {
  trackCTA,
  trackLead,
  trackPricingViewed,
  trackInvitationPreviewViewed,
  trackErosViewed,
  useSectionOnceObserver,
} from "../../lib/analytics";
// The sample-invitation URL used to be built here. It moved to lib/ when
// /digital-wedding-invitation became a second call site — same slug, same
// `?lang=` behaviour, one owner.
import { buildDemoInvitationUrl } from "../../lib/demoInvitation.js";
// Same story for the sandbox: /wedding-planning is the second call site, so the
// URL got an owner instead of a second hand-built copy.
import { buildSandboxUrl } from "../../lib/sandboxDemo.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

const PLAN_KEYS = ["blossom", "bouquet", "heirloom"];
const HEIRLOOM_KEY = "heirloom";
// Highlight the Heirloom tier — keeps parity with the dashboard's
// recommended plan (see app/src/data/wedding-pricing.ts → recommended).
const POPULAR_KEY = HEIRLOOM_KEY;
// Rows of every plan card, in order: title, tagline, price, description,
// divider, feature list, Heirloom's links (empty on the others), button. On
// desktop the cards share these rows (subgrid), so the prices, dividers and
// first checks line up however the taglines and descriptions wrap. Keep it
// equal to the card's in-flow children; the popular badge is absolute.
const CARD_ROWS = 8;

const Pricing = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const { shortLanguage } = useLanguage();
  const analyticsContext = useAnalyticsContext();
  const demoUrl = buildDemoInvitationUrl(shortLanguage);
  const sandboxUrl = buildSandboxUrl(shortLanguage);
  // Three once-per-load discovery impressions, all inside the pricing area:
  // the pricing section overall, the sample-invitation preview affordance, and
  // the Éros spotlight band. onVisible is read from a ref each render, so each
  // fires with the latest page/language.
  const sectionRef = useSectionOnceObserver(() =>
    trackPricingViewed(analyticsContext),
  );
  const invitationPreviewRef = useSectionOnceObserver(() =>
    trackInvitationPreviewViewed(analyticsContext),
  );
  const erosRef = useSectionOnceObserver(() => trackErosViewed(analyticsContext));

  const plans = PLAN_KEYS.map((key) => ({
    key,
    title: t(`home.pricing.plans.${key}.title`),
    tagline: t(`home.pricing.plans.${key}.tagline`),
    price: t(`home.pricing.plans.${key}.price`),
    description: t(`home.pricing.plans.${key}.description`),
    features: t(`home.pricing.plans.${key}.features`, { returnObjects: true }),
    // Higher-tier features shown grayed/locked on lower plans to nudge upgrades,
    // grouped by the tier that unlocks them so each group can name its plan
    // (Blossom's locked items span both Bouquet and Heirloom).
    // Missing key → [] via defaultValue, so Heirloom (top tier) renders none.
    lockedGroups: t(`home.pricing.plans.${key}.lockedGroups`, {
      returnObjects: true,
      defaultValue: [],
    }),
    buttonText: t(`home.pricing.plans.${key}.buttonText`),
    previewLink: t(`home.pricing.plans.${key}.previewLink`, {
      defaultValue: "",
    }),
    tryLink: t(`home.pricing.plans.${key}.tryLink`, { defaultValue: "" }),
    isPopular: key === POPULAR_KEY,
    isHeirloom: key === HEIRLOOM_KEY,
  }));

  return (
    <section
      id="pricing"
      ref={sectionRef}
      className="section"
      style={{ padding: "100px 0", backgroundColor: "#fff" }}
    >
      <style>
        {`
          @media (min-width: 992px) {
            .pricing-grid {
              grid-template-columns: repeat(3, 1fr) !important;
            }
          }
          @media (max-width: 991px) {
            .pricing-grid {
              grid-template-columns: 1fr !important;
            }
          }
          .pricing-card {
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          @media (min-width: 992px) {
            @supports (grid-template-rows: subgrid) {
              .pricing-grid {
                row-gap: 0 !important;
              }
              .pricing-card {
                display: grid;
                grid-row: span ${CARD_ROWS};
                grid-template-rows: subgrid;
                row-gap: 0;
                justify-items: center;
                align-items: start;
              }
            }
          }
        `}
      </style>
      <div
        className="w-container"
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "0 20px",
        }}
      >
        <div style={{ textAlign: "center", margin: "0 auto 60px auto" }}>
          <h2 className="heading-50" style={{ marginBottom: "20px" }}>
            {t("home.pricing.heading")}
          </h2>
          <p
            className="body-20"
            style={{ maxWidth: "600px", margin: "0 auto", color: "#666" }}
          >
            {t("home.pricing.intro")}
          </p>
        </div>

        <div
          className="pricing-grid"
          style={{
            display: "grid",
            gap: "30px",
            alignItems: "stretch",
            justifyContent: "center",
          }}
        >
          {plans.map((plan) => (
            <div
              key={plan.key}
              className="pricing-card"
              style={{
                border: plan.isPopular ? "2px solid #000" : "1px solid #e0e0e0",
                padding: "40px 30px",
                textAlign: "center",
                position: "relative",
                backgroundColor: plan.isPopular ? "#fafafa" : "#fff",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-5px)";
                e.currentTarget.style.boxShadow =
                  "0 10px 30px rgba(0,0,0,0.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              {plan.isPopular && (
                <div
                  style={{
                    position: "absolute",
                    top: "-15px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: "#000",
                    color: "#fff",
                    padding: "5px 15px",
                    fontSize: "12px",
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                  }}
                >
                  {t("home.pricing.popularBadge")}
                </div>
              )}

              <h3
                className="made-36"
                style={{ marginBottom: "6px", fontSize: "28px" }}
              >
                {plan.title}
              </h3>
              <p
                style={{
                  fontSize: "13px",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  color: "#888",
                  marginBottom: "18px",
                  fontStyle: "italic",
                }}
              >
                {plan.tagline}
              </p>
              <div
                style={{
                  fontSize: "42px",
                  fontWeight: "300",
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "baseline",
                  justifyContent: "center",
                }}
              >
                {plan.price}
                <span style={{ fontSize: "24px", marginRight: "4px" }}>€</span>
              </div>
              <p
                className="body-20"
                style={{
                  fontSize: "16px",
                  color: "#666",
                  marginBottom: "30px",
                }}
              >
                {plan.description}
              </p>

              <div
                style={{
                  borderTop: "1px solid #eaeaea",
                  margin: "0 0 30px 0",
                  width: "100%",
                }}
              ></div>

              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: "0 0 40px 0",
                  flexGrow: 1,
                  textAlign: "left",
                  width: "100%",
                }}
              >
                {plan.features.map((feature, i) => (
                  <li
                    key={i}
                    style={{
                      marginBottom: "15px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      fontSize: "15px",
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#000"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ marginRight: "10px", flexShrink: 0 }}
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    {feature}
                  </li>
                ))}

                {plan.lockedGroups.map((group, gi) => (
                  <React.Fragment key={`group-${gi}`}>
                    <li
                      style={{
                        marginTop: "10px",
                        marginBottom: "15px",
                        paddingTop: "16px",
                        borderTop: "1px solid #f0f0f0",
                        fontSize: "11px",
                        letterSpacing: "1.5px",
                        textTransform: "uppercase",
                        color: "#bbb",
                      }}
                    >
                      {t("home.pricing.upgradeToUnlock", {
                        plan: t(`home.pricing.plans.${group.plan}.title`),
                      })}
                    </li>
                    {group.features.map((feature, i) => (
                      <li
                        key={`locked-${gi}-${i}`}
                        style={{
                          marginBottom: "15px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          fontSize: "15px",
                          color: "#aaa",
                        }}
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#bbb"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ marginRight: "10px", flexShrink: 0 }}
                        >
                          <rect
                            x="3"
                            y="11"
                            width="18"
                            height="11"
                            rx="2"
                            ry="2"
                          ></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </React.Fragment>
                ))}
              </ul>

              {/* Always rendered, so every card has the same rows (see
                  CARD_ROWS); empty outside Heirloom. */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {plan.isHeirloom && plan.previewLink && (
                  <a
                    ref={invitationPreviewRef}
                    href={demoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackCTA("pricing_demo_preview", {
                        ...analyticsContext,
                        ctaName: "view_demo",
                        plan: HEIRLOOM_KEY,
                      })
                    }
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 13,
                      letterSpacing: "0.04em",
                      color: "#666",
                      textDecoration: "underline",
                      textUnderlineOffset: 3,
                      marginBottom: 16,
                    }}
                  >
                    {plan.previewLink}
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M7 17l9.2-9.2M17 17V7H7" />
                    </svg>
                  </a>
                )}

                {plan.isHeirloom && plan.tryLink && (
                  <a
                    href={sandboxUrl}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    onClick={() => {
                      const ctx = {
                        ...analyticsContext,
                        ctaName: "start_trial",
                        plan: HEIRLOOM_KEY,
                      };
                      trackCTA("pricing_try", ctx);
                      trackLead("pricing_try", ctx);
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 13,
                      letterSpacing: "0.12em",
                      color: "#000",
                      border: "1px solid #000",
                      padding: "12px 24px",
                      marginBottom: 30,
                      textDecoration: "none",
                      transition:
                        "background-color 0.25s ease, color 0.25s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#000";
                      e.currentTarget.style.color = "#fff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                      e.currentTarget.style.color = "#000";
                    }}
                  >
                    {plan.tryLink}
                  </a>
                )}
              </div>

              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  href={`${import.meta.env.VITE_APP_URL}/couple/create-event?plan=${plan.key}`}
                  text={plan.buttonText}
                  target="_blank"
                  onClick={() => {
                    const ctx = {
                      ...analyticsContext,
                      ctaName: "select_plan",
                      plan: plan.key,
                    };
                    trackCTA(`pricing_plan_${plan.key}`, ctx);
                    trackLead(`pricing_plan_${plan.key}`, ctx);
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Éros spotlight band — pulled out of the Heirloom column so it no
            longer lengthens the plan card, and given full width to stand on
            its own. Éros ships free with Heirloom (launch offer); the sparkle
            mark echoes the assistant's "auto-awesome" identity in the app
            (AssistantFocusHome). */}
        <div
          ref={erosRef}
          style={{
            marginTop: "56px",
            background: "#fafafa",
            color: "#111",
            border: "2px solid rgb(0, 0, 0)",
            padding: "32px 36px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
            flexWrap: "wrap",
            textAlign: "center",
          }}
        >
          <div style={{ maxWidth: "560px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                flexWrap: "wrap",
                marginBottom: "8px",
              }}
            >
              <span
                className="made-36"
                style={{ fontSize: "40px", lineHeight: 1.1 }}
              >
                {t("home.pricing.eros.name")}
              </span>
              <span
                style={{
                  fontSize: "10px",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  background: "#000",
                  color: "#fff",
                  borderRadius: "999px",
                  padding: "3px 9px",
                }}
              >
                {t("home.pricing.eros.badge")}
              </span>
            </div>
            <p
              style={{
                fontSize: "16px",
                margin: 0,
                color: "#222",
                lineHeight: 1.4,
              }}
            >
              {t("home.pricing.eros.tagline")}
            </p>
            <p
              style={{
                fontSize: "14px",
                margin: "20px 0 0",
                color: "#666",
                lineHeight: 1.55,
              }}
            >
              {t("home.pricing.eros.description")}
            </p>
            <p
              style={{
                fontSize: "12px",
                margin: "12px 0 0",
                color: "#999",
                lineHeight: 1.4,
              }}
            >
              {t("home.pricing.eros.offer")}
            </p>

            {/* CTA — navigates to the Heirloom plan in the create-event flow,
                mirroring the "Choose Heirloom" plan-card button. Sits below the
                copy. `ref=eros` lets the create-event page show an Éros-flavored
                subtitle only for arrivals from this action. */}
            <div style={{ marginTop: "22px" }}>
              <Button
                href={`${import.meta.env.VITE_APP_URL}/couple/create-event?plan=${HEIRLOOM_KEY}&ref=eros`}
                text={t("home.pricing.eros.cta")}
                target="_blank"
                onClick={() => {
                  const ctx = {
                    ...analyticsContext,
                    ctaName: "try_eros",
                    plan: HEIRLOOM_KEY,
                  };
                  trackCTA("pricing_eros", ctx);
                  trackLead("pricing_eros", ctx);
                }}
              />
            </div>
          </div>
        </div>

        {/* Distance-selling disclaimer: by selecting a paid plan, the
            couple acknowledges the service is performed in connection
            with their wedding date — the EU 14-day right of withdrawal
            does not survive performance (Directive 2011/83 art. 16(a)).
            Full text lives on the /terms page. */}
        <p
          style={{
            marginTop: "40px",
            textAlign: "center",
            fontSize: "13px",
            color: "#888",
            maxWidth: "640px",
            marginLeft: "auto",
            marginRight: "auto",
            lineHeight: 1.6,
          }}
        >
          {t("home.pricing.disclaimer")}{" "}
          <a
            href={localize("/terms")}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#888", textDecoration: "underline" }}
          >
            {t("home.pricing.disclaimer_terms_link")}
          </a>
          .
        </p>
      </div>
    </section>
  );
};

export default Pricing;
